/**
 * mockPolygon — AK-334 «Tap-to-field» dev-фоллбек детекции границ.
 *
 * Пока backend AK-333 (`POST /api/v1/fields/delineate`, SAM point-prompt по
 * спутнику) не готов, за ЯВНЫМ флагом `localStorage.delineateMock='1'` генерим
 * правдоподобный контур вокруг точки тапа — чтобы founder мог прокликать весь
 * flow (тап → черновик → редактор → форма → сохранение) на стейдже.
 *
 * ВАЖНО: только за флагом. В проде (флаг не выставлен) mock не вызывается —
 * см. delineateApi.ts. Чистая функция без сети/DOM — покрыта юнит-тестом.
 *
 * Форма: нерегулярный 6–8-угольник, площадь ~40–120 га. Радиус подбираем от
 * широты (метры→градусы), угловые шаги и радиусы слегка «дрожат», чтобы контур
 * выглядел как настоящее поле, а не как правильный многоугольник.
 */
import type { LngLat } from '../editor/fieldGeojson';

/** Детерминированный PRNG (mulberry32) — воспроизводимый mock для тестов. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Средний радиус (метры) под целевую площадь круга (га → м → r). */
function radiusForAreaHa(areaHa: number): number {
  const areaSqm = areaHa * 10_000;
  return Math.sqrt(areaSqm / Math.PI);
}

export interface MockPolygonResult {
  /** GeoJSON Polygon (замкнутое внешнее кольцо, [lng,lat]). */
  geometry: { type: 'Polygon'; coordinates: LngLat[][] };
  /** Приблизительная площадь, га. */
  area_ha: number;
}

/**
 * Сгенерировать правдоподобный контур поля вокруг точки [lng, lat].
 *
 * @param center — точка тапа [lng, lat]
 * @param seed   — сид PRNG (по умолчанию из координат — стабильно на точку)
 */
export function mockDelineate(center: LngLat, seed?: number): MockPolygonResult {
  const [lng, lat] = center;
  const rnd = mulberry32(
    seed ?? Math.floor(Math.abs(lng * 1e5) + Math.abs(lat * 1e5)) + 1,
  );

  // Целевая площадь 40–120 га → базовый радиус в метрах.
  const targetAreaHa = 40 + rnd() * 80;
  const baseR = radiusForAreaHa(targetAreaHa);

  // 6–8 вершин.
  const n = 6 + Math.floor(rnd() * 3);

  // Метры → градусы (широта фиксированная; долгота сжимается на cos(lat)).
  const mPerDegLat = 111_320;
  const mPerDegLng = 111_320 * Math.cos((lat * Math.PI) / 180) || 1;

  const ring: LngLat[] = [];
  // Стартовый угол «дрожит», шаги неравномерны, радиус ±35% — форма нерегулярна.
  let angle = rnd() * Math.PI * 2;
  for (let i = 0; i < n; i++) {
    const step = (Math.PI * 2) / n;
    // Неравномерность шага ±40% от равного деления.
    angle += step * (0.6 + rnd() * 0.8);
    const r = baseR * (0.65 + rnd() * 0.7);
    const dxM = Math.cos(angle) * r;
    const dyM = Math.sin(angle) * r;
    ring.push([
      lng + dxM / mPerDegLng,
      lat + dyM / mPerDegLat,
    ]);
  }
  // Замкнуть кольцо.
  ring.push([ring[0][0], ring[0][1]]);

  const area_ha = ringAreaHa(ring, lat);
  return {
    geometry: { type: 'Polygon', coordinates: [ring] },
    area_ha,
  };
}

/**
 * Площадь кольца (га) через плоскую shoelace в локальной метрической проекции
 * относительно широты. Достаточно точно для mock-подписи (не для агрономии).
 */
function ringAreaHa(ring: LngLat[], lat: number): number {
  const mPerDegLat = 111_320;
  const mPerDegLng = 111_320 * Math.cos((lat * Math.PI) / 180) || 1;
  let sum = 0;
  for (let i = 0; i < ring.length - 1; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[i + 1];
    const ax = x1 * mPerDegLng;
    const ay = y1 * mPerDegLat;
    const bx = x2 * mPerDegLng;
    const by = y2 * mPerDegLat;
    sum += ax * by - bx * ay;
  }
  const areaSqm = Math.abs(sum) / 2;
  return Math.round((areaSqm / 10_000) * 10) / 10;
}
